import { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import Map, { Marker, Source, Layer } from 'react-map-gl/mapbox';
import 'mapbox-gl/dist/mapbox-gl.css';
import { Navigation, X, Check, Truck, Compass, ChevronUp, ChevronDown } from 'lucide-react';
import BottomTabBar from '../components/BottomTabBar';
import LoadingSpinner from '../components/LoadingSpinner';
import { getActiveLoads } from '../api/loadsApi';
import { useTracking } from '../context/TrackingContext';
import { buildOrderedRoute } from '../utils/routeOrdering';
import { getDrivingRoute } from '../utils/directions';
import { haversineDistance } from '../utils/geocoding';
import {
  saveRouteSession,
  getRouteSession,
  clearRouteSession,
  stopKey,
} from '../utils/routeSession';
import { useToast } from '../context/ToastContext';
import mapboxgl from 'mapbox-gl';

// The following is required to stop "npm build" from transpiling mapbox code.
// notice the exclamation point in the import.
// @ts-ignore
// eslint-disable-next-line import/no-webpack-loader-syntax, import/no-unresolved
mapboxgl.workerClass = require('worker-loader!mapbox-gl/dist/mapbox-gl-csp-worker').default;

const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN;

// Route only recalculates (re-geocoding + a fresh Directions API call)
// when the driver has moved at least this far since the last
// recalculation, OR this much time has passed — whichever comes first.
// Without this, the route was recalculating on every single GPS tick
// (every few seconds), causing both unnecessary Mapbox API cost AND
// the bottom card flickering open/closed every time.
const RECALC_MIN_DISTANCE_MILES = 0.15;
const RECALC_MAX_INTERVAL_MS = 45000; // 45 seconds, as a fallback

function MapScreen() {
  const { showToast } = useToast();
  const { trackingEnabled, setTrackingEnabled, isConnected, currentPosition, heading } = useTracking();
  const [searchParams, setSearchParams] = useSearchParams();

  const [activeLoads, setActiveLoads] = useState([]);
  const [loadingLoads, setLoadingLoads] = useState(true);
  const [selectedLoadIds, setSelectedLoadIds] = useState([]);
  const [showSelector, setShowSelector] = useState(true);

  const [routeStops, setRouteStops] = useState([]);
  const [completedStops, setCompletedStops] = useState([]);
  const [routeGeometry, setRouteGeometry] = useState(null);
  const [routeSummary, setRouteSummary] = useState(null);
  const [calculatingRoute, setCalculatingRoute] = useState(false);
  const [cardCollapsed, setCardCollapsed] = useState(false);

  const [followMode, setFollowMode] = useState(true);

  const hasRestoredSession = useRef(false);
  const hasHandledLoadIdParam = useRef(false);
  const mapRef = useRef(null);
  const hasCalculatedOnceRef = useRef(false);

  // Track WHERE and WHEN the last recalculation happened, so we can
  // decide whether a new GPS update actually warrants a fresh calculation.
  const lastRecalcPositionRef = useRef(null);
  const lastRecalcTimeRef = useRef(0);

  useEffect(() => {
    async function fetchLoads() {
      try {
        const result = await getActiveLoads();
        if (result.success) {
          setActiveLoads(result.loads);
        }
      } catch (err) {
        showToast('error', 'Could not load your assigned loads.');
      } finally {
        setLoadingLoads(false);
      }
    }
    fetchLoads();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --- "Go to Road" entry point: arriving via /map?loadId=42 ---
  // Takes priority over restoring a previous session — tapping "Go to
  // Road" on a specific load is an explicit, fresh intent from the
  // driver, so it starts a new single-load session immediately rather
  // than waiting for them to use the selector screen.
  useEffect(() => {
    if (hasHandledLoadIdParam.current || loadingLoads) return;

    const requestedLoadId = searchParams.get('loadId');
    if (!requestedLoadId) return;

    const matchingLoad = activeLoads.find((l) => String(l.id_load) === String(requestedLoadId));

    hasHandledLoadIdParam.current = true;
    hasRestoredSession.current = true; // also skip the normal session-restore effect below

    if (matchingLoad) {
      setSelectedLoadIds([matchingLoad.id_load]);
      setCompletedStops([]);
      hasCalculatedOnceRef.current = false;
      lastRecalcPositionRef.current = null;
      lastRecalcTimeRef.current = 0;
      setShowSelector(false);
      setFollowMode(true);
      saveRouteSession([matchingLoad.id_load], []);
      setTrackingEnabled(true);
    } else {
      showToast('error', 'That load is no longer available for tracking.');
    }

    // Clean the query param out of the URL so refreshing/navigating
    // back doesn't re-trigger this same auto-start logic.
    setSearchParams({}, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingLoads, activeLoads]);

  useEffect(() => {
    if (hasRestoredSession.current || loadingLoads) return;
    hasRestoredSession.current = true;

    const session = getRouteSession();
    if (session && session.selectedLoadIds.length > 0) {
      setSelectedLoadIds(session.selectedLoadIds);
      setCompletedStops(session.completedStopKeys || []);
      setShowSelector(false);
      setTrackingEnabled(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingLoads]);

  // --- Route (re)calculation, throttled ---
  // Only shows the "Calculating route..." loading state the very FIRST
  // time (before we've ever had a result). Every subsequent
  // recalculation happens silently in the background, updating the
  // stop/route info in place once ready — no flicker.
  useEffect(() => {
    async function calculateRoute() {
      if (!currentPosition || selectedLoadIds.length === 0 || showSelector) return;

      const isFirstCalculation = !hasCalculatedOnceRef.current;

      if (!isFirstCalculation) {
        const distanceMoved = lastRecalcPositionRef.current
          ? haversineDistance(lastRecalcPositionRef.current, currentPosition)
          : Infinity;
        const timeSinceLastRecalc = Date.now() - lastRecalcTimeRef.current;

        const shouldRecalculate =
          distanceMoved >= RECALC_MIN_DISTANCE_MILES || timeSinceLastRecalc >= RECALC_MAX_INTERVAL_MS;

        if (!shouldRecalculate) return;
      }

      if (isFirstCalculation) {
        setCalculatingRoute(true);
      }

      try {
        const loadsToRoute = activeLoads.filter((l) => selectedLoadIds.includes(l.id_load));
        const ordered = await buildOrderedRoute(currentPosition, loadsToRoute);

        const remainingStops = ordered.filter((stop) => !completedStops.includes(stopKey(stop)));
        setRouteStops(remainingStops);

        if (remainingStops.length > 0) {
          const coordsForDirections = [currentPosition, ...remainingStops];
          const route = await getDrivingRoute(coordsForDirections);
          if (route) {
            setRouteGeometry(route.geometry);
            setRouteSummary({ distanceMiles: route.distanceMiles, durationMinutes: route.durationMinutes });
          }
        } else {
          setRouteGeometry(null);
          setRouteSummary(null);
        }

        hasCalculatedOnceRef.current = true;
        lastRecalcPositionRef.current = currentPosition;
        lastRecalcTimeRef.current = Date.now();
      } catch (err) {
        if (isFirstCalculation) {
          showToast('error', 'Could not calculate route.');
        }
      } finally {
        if (isFirstCalculation) {
          setCalculatingRoute(false);
        }
      }
    }

    calculateRoute();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPosition, selectedLoadIds, showSelector]);

  useEffect(() => {
    if (!currentPosition || !mapRef.current || !followMode) return;

    mapRef.current.easeTo({
      center: [currentPosition.lng, currentPosition.lat],
      bearing: heading !== null ? heading : mapRef.current.getBearing(),
      zoom: Math.max(mapRef.current.getZoom(), 15),
      duration: 800,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPosition, heading, followMode]);

  useEffect(() => {
    if (currentPosition && mapRef.current) {
      mapRef.current.flyTo({
        center: [currentPosition.lng, currentPosition.lat],
        zoom: 15,
        duration: 1000,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [Boolean(currentPosition)]);

  const handleRecenter = () => {
    setFollowMode(true);
    if (currentPosition && mapRef.current) {
      mapRef.current.easeTo({
        center: [currentPosition.lng, currentPosition.lat],
        bearing: heading !== null ? heading : 0,
        zoom: 15,
        duration: 800,
      });
    }
  };

  const handleUserDragStart = () => {
    setFollowMode(false);
  };

  const toggleLoadSelection = (loadId) => {
    setSelectedLoadIds((prev) =>
      prev.includes(loadId) ? prev.filter((id) => id !== loadId) : [...prev, loadId]
    );
  };

  const handleStartRoute = () => {
    if (selectedLoadIds.length === 0) {
      showToast('error', 'Select at least one load first.');
      return;
    }
    setShowSelector(false);
    setCompletedStops([]);
    hasCalculatedOnceRef.current = false;
    lastRecalcPositionRef.current = null;
    lastRecalcTimeRef.current = 0;
    setFollowMode(true);
    saveRouteSession(selectedLoadIds, []);
    setTrackingEnabled(true);
  };

  const handleEndRoute = () => {
    clearRouteSession();
    setSelectedLoadIds([]);
    setRouteStops([]);
    setRouteGeometry(null);
    setRouteSummary(null);
    setCompletedStops([]);
    setShowSelector(true);
    setTrackingEnabled(false);
    hasCalculatedOnceRef.current = false;
    lastRecalcPositionRef.current = null;
    lastRecalcTimeRef.current = 0;
  };

  const handleMarkStopDone = (stop) => {
    const key = stopKey(stop);
    const updatedCompleted = [...completedStops, key];
    setCompletedStops(updatedCompleted);
    saveRouteSession(selectedLoadIds, updatedCompleted);

    setRouteStops((prev) => prev.filter((s) => stopKey(s) !== key));

    // Marking a stop done should trigger an immediate recalculation
    // next tick, not wait for the throttle window — reset the tracker.
    lastRecalcPositionRef.current = null;

    if (routeStops.length <= 1) {
      showToast('success', 'Route complete!');
      handleEndRoute();
    }
  };

  const validRouteStops = routeStops.filter(
    (stop) =>
      typeof stop.lat === 'number' &&
      typeof stop.lng === 'number' &&
      !isNaN(stop.lat) &&
      !isNaN(stop.lng)
  );

  const hasValidCurrentPosition =
    currentPosition &&
    typeof currentPosition.lat === 'number' &&
    typeof currentPosition.lng === 'number' &&
    !isNaN(currentPosition.lat) &&
    !isNaN(currentPosition.lng);

  const truckIconRotation = followMode ? 0 : (heading || 0);

  if (loadingLoads) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: '#f9fafb' }}>
        <LoadingSpinner fullScreen />
        <BottomTabBar />
      </div>
    );
  }

  return (
    <div style={styles.wrapper}>
      {showSelector && (
        <div style={styles.selectorPanel}>
          <div style={styles.selectorScroll}>
            <div style={styles.selectorTitle}>Select today's loads</div>
            {activeLoads.length === 0 ? (
              <p style={{ color: '#9ca3af', fontSize: 13 }}>No active loads assigned right now.</p>
            ) : (
              <div style={styles.loadList}>
                {activeLoads.map((load) => {
                  const isSelected = selectedLoadIds.includes(load.id_load);
                  return (
                    <div
                      key={load.id_load}
                      onClick={() => toggleLoadSelection(load.id_load)}
                      style={{
                        ...styles.loadItem,
                        backgroundColor: isSelected ? '#eff6ff' : 'white',
                        borderColor: isSelected ? '#2563eb' : '#e5e7eb',
                      }}
                    >
                      <div
                        style={{
                          ...styles.checkbox,
                          backgroundColor: isSelected ? '#2563eb' : 'white',
                        }}
                      >
                        {isSelected && <Check size={12} color="white" />}
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={styles.loadRoute}>
                          {load.origin} <span style={{ color: '#9ca3af' }}>→</span> {load.destination}
                        </div>
                        <div style={styles.loadStatus}>{load.load_status}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <button onClick={handleStartRoute} style={styles.floatingStartBtn}>
            <Navigation size={16} style={{ marginRight: 8 }} />
            Start Route ({selectedLoadIds.length} selected)
          </button>
        </div>
      )}

      {!showSelector && (
        <>
          <Map
            ref={mapRef}
            mapboxAccessToken={MAPBOX_TOKEN}
            initialViewState={{
              longitude: currentPosition?.lng || -96.8,
              latitude: currentPosition?.lat || 32.8,
              zoom: 10,
            }}
            style={{ width: '100%', height: '100vh' }}
            mapStyle="mapbox://styles/mapbox/streets-v12"
            onDragStart={handleUserDragStart}
          >
            {hasValidCurrentPosition && (
              <Marker longitude={currentPosition.lng} latitude={currentPosition.lat}>
                <div
                  style={{
                    ...styles.driverMarker,
                    transform: `rotate(${truckIconRotation}deg)`,
                  }}
                >
                  <Truck size={16} color="white" />
                </div>
              </Marker>
            )}

            {validRouteStops.map((stop, index) => (
              <Marker key={stopKey(stop)} longitude={stop.lng} latitude={stop.lat}>
                <div
                  style={{
                    ...styles.stopMarker,
                    backgroundColor: stop.type === 'pickup' ? '#2563eb' : '#059669',
                  }}
                >
                  {index + 1}
                </div>
              </Marker>
            ))}

            {routeGeometry && (
              <Source id="route" type="geojson" data={{ type: 'Feature', geometry: routeGeometry }}>
                <Layer
                  id="route-line"
                  type="line"
                  paint={{ 'line-color': '#2563eb', 'line-width': 4 }}
                />
              </Source>
            )}
          </Map>

          <button onClick={handleRecenter} style={styles.recenterBtn}>
            {followMode ? (
              <Navigation size={20} color="#2563eb" fill="#2563eb" />
            ) : (
              <Compass size={20} color="#2563eb" />
            )}
          </button>

          <div
            style={{
              ...styles.bottomCard,
              padding: cardCollapsed
                ? '10px 20px calc(10px + env(safe-area-inset-bottom))'
                : '14px 20px calc(20px + env(safe-area-inset-bottom))',
            }}
          >
            <div style={styles.cardHeader}>
              <span style={{ fontSize: 12, color: isConnected ? '#059669' : '#dc2626', fontWeight: 700 }}>
                {isConnected ? '● Live tracking active' : '○ Connecting...'}
              </span>
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={() => setCardCollapsed(!cardCollapsed)} style={styles.collapseBtn}>
                  {cardCollapsed ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                </button>
                <button onClick={handleEndRoute} style={styles.endBtn}>
                  <X size={14} style={{ marginRight: 4 }} /> End Route
                </button>
              </div>
            </div>

            {!cardCollapsed && (
              <>
                {calculatingRoute && <p style={{ fontSize: 13, color: '#9ca3af' }}>Calculating route...</p>}

                {!calculatingRoute && validRouteStops.length > 0 && (
                  <>
                    <div style={styles.nextStopLabel}>
                      Next: {validRouteStops[0].type === 'pickup' ? 'Pickup' : 'Dropoff'}
                    </div>
                    <div style={styles.nextStopAddress}>{validRouteStops[0].address}</div>
                    {routeSummary && (
                      <div style={styles.routeMeta}>
                        {routeSummary.distanceMiles} mi • {routeSummary.durationMinutes} min
                      </div>
                    )}
                    <button onClick={() => handleMarkStopDone(validRouteStops[0])} style={styles.doneBtn}>
                      Mark as Done
                    </button>
                  </>
                )}

                {!calculatingRoute && validRouteStops.length === 0 && (
                  <p style={{ fontSize: 13, color: '#059669', fontWeight: 700 }}>All stops complete!</p>
                )}
              </>
            )}
          </div>
        </>
      )}

      <BottomTabBar />
    </div>
  );
}

const styles = {
  wrapper: { position: 'relative', minHeight: '100vh' },
  selectorPanel: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 'calc(64px + env(safe-area-inset-bottom))',
    backgroundColor: '#f9fafb',
    zIndex: 10,
    display: 'flex',
    flexDirection: 'column',
  },
  selectorScroll: {
    flex: 1,
    overflowY: 'auto',
    padding: '16px 16px 90px',
  },
  selectorTitle: { fontSize: 16, fontWeight: 700, color: '#111827', marginBottom: 12 },
  loadList: { display: 'flex', flexDirection: 'column', gap: 8 },
  loadItem: {
    display: 'flex',
    alignItems: 'center',
    padding: 12,
    borderRadius: 10,
    border: '1px solid',
    cursor: 'pointer',
  },
  checkbox: {
    width: 20,
    height: 20,
    borderRadius: 6,
    border: '1px solid #d1d5db',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 12,
    flexShrink: 0,
  },
  loadRoute: { fontSize: 14, fontWeight: 600, color: '#111827' },
  loadStatus: { fontSize: 11, color: '#9ca3af', textTransform: 'capitalize' },
  floatingStartBtn: {
    position: 'absolute',
    bottom: 'calc(16px + env(safe-area-inset-bottom))',
    left: 16,
    right: 16,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#2563eb',
    color: 'white',
    fontWeight: 700,
    fontSize: 15,
    padding: '14px 0',
    borderRadius: 12,
    border: 'none',
    cursor: 'pointer',
    boxShadow: '0 4px 14px rgba(37,99,235,0.35)',
  },
  driverMarker: {
    width: 30,
    height: 30,
    borderRadius: '50%',
    backgroundColor: '#2563eb',
    border: '3px solid white',
    boxShadow: '0 0 0 4px rgba(37,99,235,0.25)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'transform 0.3s ease',
  },
  stopMarker: {
    width: 26,
    height: 26,
    borderRadius: '50%',
    color: 'white',
    fontSize: 12,
    fontWeight: 700,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: '2px solid white',
    boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
  },
  recenterBtn: {
    position: 'fixed',
    right: 16,
    bottom: 'calc(140px + env(safe-area-inset-bottom))',
    width: 44,
    height: 44,
    borderRadius: '50%',
    backgroundColor: 'white',
    border: 'none',
    boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    zIndex: 25,
  },
  bottomCard: {
    position: 'fixed',
    bottom: 64,
    left: 0,
    right: 0,
    backgroundColor: 'white',
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    boxShadow: '0 -2px 12px rgba(0,0,0,0.1)',
    zIndex: 20,
    transition: 'padding 0.2s ease',
  },
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 10,
  },
  collapseBtn: {
    background: '#f3f4f6',
    border: 'none',
    borderRadius: 8,
    padding: '6px 8px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
  },
  endBtn: {
    display: 'flex',
    alignItems: 'center',
    backgroundColor: '#fee2e2',
    color: '#991b1b',
    border: 'none',
    borderRadius: 8,
    padding: '6px 12px',
    fontSize: 12,
    fontWeight: 700,
    cursor: 'pointer',
  },
  nextStopLabel: { fontSize: 11, color: '#9ca3af', fontWeight: 700, textTransform: 'uppercase' },
  nextStopAddress: { fontSize: 15, fontWeight: 700, color: '#111827', marginTop: 2, marginBottom: 4 },
  routeMeta: { fontSize: 12, color: '#6b7280', marginBottom: 12 },
  doneBtn: {
    width: '100%',
    backgroundColor: '#2563eb',
    color: 'white',
    fontWeight: 700,
    fontSize: 14,
    padding: '12px 0',
    borderRadius: 10,
    border: 'none',
    cursor: 'pointer',
  },
};

export default MapScreen; 